import { formatDiscountForDisplay } from 'shared/helpers/specials';

import type { DiscountCalculationMethod } from 'src/gql/graphql';
import type CartItemClass from 'src/state/cart-item';

export type DiscountBadgeType = 'bogo' | 'sale' | null;

export type DeriveDiscountBadgeParams = {
  originalPrice: string;
  bogoDiscountedPrice: string | null;
  discountedPrice: string;
  item: CartItemClass;
  shouldHideDiscounts: boolean;
  isWeightOnSale: boolean;
  isMobile: boolean;
  isPriceCartEnabled: boolean;
  // Price-cart's per-item calculationMethod (see CartItemPrice in use-cart-pricing.types.ts). When
  // present, this is the real source of truth for percent-vs-dollar and overrides the saleSpecials
  // heuristic inside formatDiscountForDisplay for the 'sale' badge type.
  calculationMethod?: DiscountCalculationMethod | null;
};

/** Pure per-line badge derivation, shared by `useDiscountBadgeText` (hook, single
 * render) and `mapCartLine` (concierge bridge, loop). The two hook-sourced inputs
 * — `isMobile` and `isPriceCartEnabled` — are passed in so this stays callable in
 * a loop. Keep in lockstep with `useDiscountBadgeText`. */
export const deriveDiscountBadge = ({
  originalPrice,
  bogoDiscountedPrice,
  discountedPrice,
  item,
  shouldHideDiscounts,
  isWeightOnSale,
  isMobile,
  isPriceCartEnabled,
  calculationMethod = null,
}: DeriveDiscountBadgeParams): { discountBadgeText: string | null; type: DiscountBadgeType } => {
  if (shouldHideDiscounts) {
    return { discountBadgeText: null, type: null };
  }

  let type: DiscountBadgeType = null;
  let discountedPriceToUse = '';

  if (bogoDiscountedPrice) {
    type = 'bogo';
    discountedPriceToUse = bogoDiscountedPrice;
  } else if (isWeightOnSale) {
    type = 'sale';
    discountedPriceToUse = discountedPrice;
  }

  const discountBadgeText = formatDiscountForDisplay(
    parseFloat(originalPrice),
    parseFloat(discountedPriceToUse),
    item,
    type,
    item.option,
    isMobile,
    isPriceCartEnabled,
    calculationMethod
  );

  return { discountBadgeText, type };
};
